import { create, createStore } from 'zustand';
import type { StateCreator, StoreApi } from 'zustand';
import { createContext, ReactNode } from 'react';
import {
  createNewSession,
  createNewSessionCollection,
} from 'react-scale/components/WebinarSessions/api';
import {
  CohortMappingInput,
  ICreateSessionAPIParams,
  RecurringType,
} from '@scale/shared/webinarSessions/types';
import { isNil } from 'lodash';
import {
  timeDifferenceInMinutes,
  getNewStartDateTime,
  getStartTimeFromDate,
  getEndTimeFromStartTime,
} from 'react-scale/components/WebinarSessions/hooks/calculateFutureOccurrences';
import { SessionTime, RepeatType } from 'react-scale/components/WebinarSessions/types';
import { WebinarError } from 'react-scale/components/WebinarSessions/constants';

export interface CreateWebinarSessionStore {
  // General fields
  name: string | null;
  setName: (name: string | null) => void;
  description: string | null;
  setDescription: (description: string | null) => void;
  eventStart: Date | null;
  setEventStart: (d: Date | null) => void;
  duration: number | null;
  setDuration: (name: number | null) => void;
  hostedBy: string[];
  setHostedBy: (ids: string[]) => void;
  rsvpMax: number | null;
  setRsvpMax: (n: number | null) => void;
  minAttendanceDuration: number | null;
  setMinAttendanceDuration: (n: number | null) => void;
  expectedNumberOfAttendees: number | null;
  setExpectedNumberOfAttendees: (n: number | null) => void;
  project: string | null;
  setProject: (id: string | null) => void;

  // Session fields
  sessionType: RecurringType;
  setSessionType: (sessionType: RecurringType) => void;
  // Recurring session fields
  occurrences: Date[] | null;
  setOccurrences: (o: Date[] | null) => void;

  // Invitees fields
  invitedUsers: string[];
  setInvitedUsers: (ids: string[]) => void;
  audience: string | null;
  setAudience: (id: string | null | undefined) => void;
  audienceBucket: number | null;
  setAudienceBucket: (index: number | null | undefined) => void;
  invitedWorkerTeams: string[];
  setInvitedWorkerTeams: (ids: string[]) => void;
  invitedQualifications: string[];
  setInvitedQualifications: (ids: string[]) => void;
  invitedSquads: string[];
  setInvitedSquads: (ids: string[]) => void;
  allInvitedUserIds: string[];
  setAllInvitedUserIds: (ids: string[]) => void;
  cohortMappings: CohortMappingInput[];
  setCohortMappings: (mappings: CohortMappingInput[]) => void;

  // Communications fields
  sendInvitationEmails: boolean;
  setSendInvitationEmails: (b: boolean) => void;
  send30MinuteReminderEmails: boolean;
  setSend30MinuteReminderEmails: (b: boolean) => void;
  excludeEmptyQueueUsers: boolean;
  setExcludeEmptyQueueUsers: (b: boolean) => void;

  // V2 specific creation fields
  isV2Creation: boolean;
  numOccurrences: number | null;
  setNumOccurrences: (n: number | null) => void;
  selectedDate: Date;
  setSelectedDate: (d: Date) => void;
  startTime: string;
  setStartTime: (t: string) => void;
  endTime: string;
  setEndTime: (t: string) => void;
  repeatNumber: number | null;
  setRepeatNumber: (n: number | null) => void;
  repeatType: RepeatType;
  setRepeatType: (t: RepeatType) => void;
  daysOfTheWeek: string[];
  setDaysOfTheWeek: (d: string[]) => void;
  additionalTimesOfDay: string[];
  setAdditionalTimesOfDay: (t: string[]) => void;
  // Multiple session fields
  additionalTimesOfDayV2: SessionTime[];
  setAdditionalTimesOfDayV2: (o: SessionTime[]) => void;
  occurencesV2: SessionTime[];
  setOccurencesV2: (o: SessionTime[]) => void;
  additionalSessions: SessionTime[];
  setAdditionalSessions: (o: SessionTime[]) => void;
  isProjectCreation: boolean;
  setIsProjectCreation: (b: boolean) => void;

  // Misc fields
  createSessions: () => Promise<void>;
  createdNewSessions: number;
  newlyCreatedSessions: any[];
  reloadSessions: () => void;
  getErrors: () => WebinarError[];
  getWebinarSettingsErrors: () => WebinarError[];
  getWebinarInviteeErrors: () => WebinarError[];
  getWebinarSessionErrors: () => WebinarError[];
  getRecurringErrors: () => Record<number, WebinarError>;
  getAdditionalSessionsErrors: () => Record<number, WebinarError>;
}

const createWebinarSessionStoreImpl: StateCreator<CreateWebinarSessionStore> = (set, get) => ({
  name: null,
  setName: name => set({ name }),
  eventStart: null,
  setEventStart: eventStart => set({ eventStart }),
  duration: null,
  setDuration: duration => set({ duration }),
  occurrences: null,
  setOccurrences: occurrences => set({ occurrences }),
  additionalTimesOfDayV2: [],
  setAdditionalTimesOfDayV2: additionalTimesOfDayV2 => set({ additionalTimesOfDayV2 }),
  occurencesV2: [],
  setOccurencesV2: occurencesV2 => set({ occurencesV2 }),
  additionalSessions: [],
  setAdditionalSessions: additionalSessions => set({ additionalSessions }),
  description: null,
  setDescription: description => set({ description }),
  rsvpMax: null,
  setRsvpMax: rsvpMax => set({ rsvpMax }),
  minAttendanceDuration: null,
  setMinAttendanceDuration: minAttendanceDuration => set({ minAttendanceDuration }),
  expectedNumberOfAttendees: null,
  setExpectedNumberOfAttendees: expectedNumberOfAttendees => set({ expectedNumberOfAttendees }),
  project: null,
  setProject: project => set({ project }),
  invitedUsers: [],
  setInvitedUsers: invitedUsers => set({ invitedUsers }),
  audience: null,
  setAudience: audience => set({ audience }),
  audienceBucket: null,
  setAudienceBucket: audienceBucket => set({ audienceBucket }),
  invitedWorkerTeams: [],
  setInvitedWorkerTeams: invitedWorkerTeams => set({ invitedWorkerTeams }),
  invitedQualifications: [],
  setInvitedQualifications: invitedQualifications => set({ invitedQualifications }),
  invitedSquads: [],
  setInvitedSquads: invitedSquads => set({ invitedSquads }),
  allInvitedUserIds: [],
  setAllInvitedUserIds: allInvitedUserIds => set({ allInvitedUserIds }),
  cohortMappings: [],
  setCohortMappings: cohortMappings => set({ cohortMappings }),
  hostedBy: [],
  setHostedBy: hostedBy => set({ hostedBy }),
  createSessions: async () => {
    const {
      name,
      eventStart,
      duration,
      invitedUsers,
      invitedWorkerTeams,
      invitedQualifications,
      invitedSquads,
      hostedBy,
      audience,
      audienceBucket,
      description,
      rsvpMax,
      minAttendanceDuration,
      expectedNumberOfAttendees,
      project,
      occurrences,
      createdNewSessions,
      sendInvitationEmails,
      send30MinuteReminderEmails,
      additionalSessions,
      sessionType,
      isProjectCreation,
      isV2Creation,
      occurencesV2,
      excludeEmptyQueueUsers,
      cohortMappings,
    } = get();
    const communications = {
      sendInvitationEmails,
      send30MinuteReminderEmails,
      excludeEmptyQueueUsers,
    };
    const params: ICreateSessionAPIParams = {
      name: name as string,
      eventStart: eventStart?.toJSON() as string,
      duration: duration as number,
      invitedUsers,
      invitedWorkerTeams,
      invitedQualifications,
      invitedSquads,
      hostedBy,
      excludeEmptyQueueUsers,
      cohortMappings,
    };
    if (audience) params.audience = audience as string;
    if (audienceBucket) params.audienceBucket = audienceBucket as number;
    if (description) params.description = description as string;
    if (rsvpMax) params.rsvpMax = rsvpMax as number;
    if (minAttendanceDuration) params.minAttendanceDuration = minAttendanceDuration as number;
    if (expectedNumberOfAttendees)
      params.expectedNumberOfAttendees = expectedNumberOfAttendees as number;
    if (project) params.project = project as string;

    const validOccurences = (occurrences && occurrences?.length) || occurencesV2.length > 0;
    if (validOccurences && sessionType === RecurringType.RECURRING) {
      params.recurringType = RecurringType.RECURRING;
    } else if (additionalSessions.length > 0 && sessionType === RecurringType.MULTI) {
      params.recurringType = RecurringType.MULTI;
    } else {
      params.recurringType = RecurringType.SINGLE;
    }

    if (params.recurringType !== RecurringType.SINGLE) {
      params.occurrences = occurrences?.map(o => o.toJSON());
      params.occurencesV2 = occurencesV2.map(o => ({
        eventStart: getNewStartDateTime(o.date, o.startTime).toJSON(),
        duration: timeDifferenceInMinutes(o.endTime, o.startTime),
        hostedBy: o.hostedBy,
      }));
      params.additionalSessions = additionalSessions.map(o => ({
        eventStart: getNewStartDateTime(o.date, o.startTime).toJSON(),
        duration: timeDifferenceInMinutes(o.endTime, o.startTime),
        hostedBy: o.hostedBy,
      }));
      const result = await createNewSessionCollection(params, communications);
      set({ newlyCreatedSessions: result.sessions });
    } else {
      const result = await createNewSession(params, communications);
      set({ newlyCreatedSessions: [result.session] });
    }
    set({ createdNewSessions: createdNewSessions + 1 });

    // Reset the fields
    const now = new Date();
    set({
      name: null,
      description: null,
      eventStart: null,
      duration: null,
      hostedBy: [],
      rsvpMax: null,
      minAttendanceDuration: null,
      expectedNumberOfAttendees: null,
      project: isProjectCreation ? project : null,
      sessionType: isV2Creation ? RecurringType.MULTI : RecurringType.SINGLE,
      occurrences: null,
      invitedUsers: [],
      invitedWorkerTeams: [],
      invitedQualifications: [],
      invitedSquads: [],
      audience: null,
      audienceBucket: null,
      sendInvitationEmails: false,
      send30MinuteReminderEmails: false,
      excludeEmptyQueueUsers: false,
      // v2 specific fields
      numOccurrences: null,
      selectedDate: now,
      startTime: getStartTimeFromDate(now),
      endTime: getEndTimeFromStartTime(getStartTimeFromDate(now)),
      repeatNumber: null,
      repeatType: RepeatType.DAYS,
      daysOfTheWeek: [],
      additionalTimesOfDay: [],
      additionalTimesOfDayV2: [],
      occurencesV2: [],
      additionalSessions: [],
      cohortMappings: [],
    });
  },
  createdNewSessions: 0,
  newlyCreatedSessions: [],
  sendInvitationEmails: false,
  setSendInvitationEmails: b => set({ sendInvitationEmails: b }),
  send30MinuteReminderEmails: false,
  setSend30MinuteReminderEmails: b => set({ send30MinuteReminderEmails: b }),
  excludeEmptyQueueUsers: false,
  setExcludeEmptyQueueUsers: b => set({ excludeEmptyQueueUsers: b }),
  reloadSessions: () => set({ createdNewSessions: get().createdNewSessions + 1 }),
  getErrors: () => {
    const errors: WebinarError[] = [];
    const { getWebinarSettingsErrors, getWebinarInviteeErrors, getWebinarSessionErrors } = get();
    errors.push(...getWebinarSettingsErrors());
    errors.push(...getWebinarInviteeErrors());
    errors.push(...getWebinarSessionErrors());
    return errors;
  },
  getWebinarSettingsErrors: () => {
    const errors: WebinarError[] = [];
    const { name } = get();

    if (!name || !name?.length) {
      errors.push(WebinarError.NAME_REQUIRED);
    }
    return errors;
  },
  getWebinarInviteeErrors: () => {
    const errors: WebinarError[] = [];
    const {
      invitedUsers,
      invitedWorkerTeams,
      invitedQualifications,
      invitedSquads,
      audience,
      cohortMappings,
    } = get();

    if (
      !invitedUsers.length &&
      !invitedWorkerTeams.length &&
      !invitedQualifications.length &&
      !invitedSquads.length &&
      !audience &&
      !cohortMappings.length
    ) {
      errors.push(WebinarError.INVITEES_REQUIRED);
    }
    return errors;
  },
  getWebinarSessionErrors: () => {
    const errors: WebinarError[] = [];
    const {
      eventStart,
      duration,
      sessionType,
      occurrences,
      occurencesV2,
      getRecurringErrors,
      getAdditionalSessionsErrors,
    } = get();

    if (!eventStart) {
      errors.push(WebinarError.START_TIME_REQUIRED);
    }
    if (eventStart && eventStart < new Date()) {
      errors.push(WebinarError.START_TIME_IN_PAST);
    }
    if (isNil(duration)) {
      errors.push(WebinarError.START_AND_END_TIME_REQUIRED);
    }
    if ((duration ?? 0) <= 0) {
      errors.push(WebinarError.END_TIME_BEFORE_START_TIME);
    }

    if (sessionType === RecurringType.RECURRING) {
      const missingOccurences = (!occurrences || !occurrences.length) && !occurencesV2.length;
      if (missingOccurences) {
        errors.push(WebinarError.MISSING_RECURRING_SESSION_ERROR);
      }
      const recurringSessionErrors = getRecurringErrors();
      if (Object.values(recurringSessionErrors).some(Boolean)) {
        errors.push(WebinarError.RECURRING_SESSION_ERROR);
      }
    }
    if (sessionType === RecurringType.MULTI) {
      const additionalSessionErrors = getAdditionalSessionsErrors();
      if (Object.values(additionalSessionErrors).some(Boolean)) {
        errors.push(WebinarError.ADDITIONAL_SESSION_ERROR);
      }
    }

    return errors;
  },
  getRecurringErrors: () => {
    const errors: Record<number, WebinarError> = {};
    const { additionalTimesOfDay, selectedDate, additionalTimesOfDayV2 } = get();

    if (additionalTimesOfDay && additionalTimesOfDay?.length) {
      additionalTimesOfDay.forEach((time, index) => {
        const eventStart = getNewStartDateTime(selectedDate, time);
        if (eventStart < new Date()) {
          errors[index] = WebinarError.START_TIME_IN_PAST;
        }
      });
    }

    if (additionalTimesOfDayV2 && additionalTimesOfDayV2?.length) {
      additionalTimesOfDayV2.forEach((sessionTime, index) => {
        const eventStart = getNewStartDateTime(sessionTime.date, sessionTime.startTime);
        const duration = timeDifferenceInMinutes(sessionTime.endTime, sessionTime.startTime);

        if (eventStart < new Date()) {
          errors[index] = WebinarError.START_TIME_IN_PAST;
        }

        if (duration <= 0) {
          errors[index] = WebinarError.END_TIME_BEFORE_START_TIME;
        }
      });
    }

    return errors;
  },
  getAdditionalSessionsErrors: () => {
    const errors: Record<number, WebinarError> = {};
    const { additionalSessions } = get();
    if (additionalSessions && additionalSessions?.length) {
      additionalSessions.forEach((session, index) => {
        const eventStart = getNewStartDateTime(session.date, session.startTime);
        const duration = timeDifferenceInMinutes(session.endTime, session.startTime);

        if (eventStart < new Date()) {
          errors[index] = WebinarError.START_TIME_IN_PAST;
        }
        if (duration <= 0) {
          errors[index] = WebinarError.END_TIME_BEFORE_START_TIME;
        }
      });
    }
    return errors;
  },
  // v2 specific fields
  sessionType: RecurringType.SINGLE,
  setSessionType: sessionType => set({ sessionType }),
  isV2Creation: false,
  numOccurrences: null,
  setNumOccurrences: numOccurrences => set({ numOccurrences }),
  selectedDate: new Date(),
  setSelectedDate: selectedDate => set({ selectedDate }),
  startTime: getStartTimeFromDate(new Date()),
  setStartTime: startTime => set({ startTime }),
  endTime: getEndTimeFromStartTime(getStartTimeFromDate(new Date())),
  setEndTime: endTime => set({ endTime }),
  repeatNumber: null,
  setRepeatNumber: repeatNumber => set({ repeatNumber }),
  repeatType: RepeatType.DAYS,
  setRepeatType: repeatType => set({ repeatType }),
  daysOfTheWeek: [],
  setDaysOfTheWeek: daysOfTheWeek => set({ daysOfTheWeek }),
  additionalTimesOfDay: [],
  setAdditionalTimesOfDay: additionalTimesOfDay => set({ additionalTimesOfDay }),
  isProjectCreation: false,
  setIsProjectCreation: isProjectCreation => set({ isProjectCreation }),
});

// Backwards compability with existing store implementation
export const useCreateWebinarSession = create<CreateWebinarSessionStore>(
  createWebinarSessionStoreImpl,
);

// New code should use createWebinarSessionStore and the useCreateWebinarSessionStore hook instead
export const createWebinarSessionStore = (initialState?: Partial<CreateWebinarSessionStore>) => {
  const store = createStore<CreateWebinarSessionStore>(createWebinarSessionStoreImpl);

  if (initialState) {
    store.setState(initialState);
  }

  return store;
};

const CreateWebinarSessionStoreContext = createContext<StoreApi<CreateWebinarSessionStore> | null>(
  null,
);

export const CreateWebinarSessionStoreProvider = ({
  children,
  store,
}: {
  children: ReactNode;
  store: StoreApi<CreateWebinarSessionStore>;
}) => {
  return (
    <CreateWebinarSessionStoreContext.Provider value={store}>
      {children}
    </CreateWebinarSessionStoreContext.Provider>
  );
};
